import type { Resume, ResumeContact } from '../types'

/**
 * crypto.randomUUID() with a fallback for older browsers.
 * Mirrors the pattern used by ApplicationsContext.createId().
 */
export function resumeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Build the default blank resume payload for a new authoring session.
 * Pre-fills name from user profile and email from auth when available.
 */
export function createBlankResume(opts: {
  title?: string
  displayName?: string | null
  email?: string | null
}): Pick<
  Resume,
  | 'title'
  | 'template'
  | 'isMaster'
  | 'tailoredForApplicationId'
  | 'contact'
  | 'summary'
  | 'experience'
  | 'education'
  | 'skills'
  | 'projects'
  | 'certifications'
  | 'languages'
  | 'customSections'
> {
  const contact: ResumeContact = { accentColor: '#0ea5e9', locale: 'en' }
  if (opts.displayName) contact.fullName = opts.displayName
  if (opts.email) contact.email = opts.email
  return {
    title: opts.title ?? 'Untitled resume',
    template: 'modern',
    isMaster: false,
    tailoredForApplicationId: null,
    contact,
    summary: null,
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    languages: [],
    customSections: [],
  }
}

/**
 * Sanitize a string for use inside a filename. Keeps letters/digits/dash/space.
 */
export function sanitizeFilename(input: string): string {
  return input
    .replace(/[^\p{L}\p{N} _-]+/gu, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 60) || 'resume'
}

export function buildResumeFilename(resume: Resume): string {
  const name = resume.contact?.fullName?.trim() || 'resume'
  return `${sanitizeFilename(name)}-${sanitizeFilename(resume.title)}.pdf`
}
